<script setup lang="ts">
interface Props {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
});

defineEmits<{ click: [event: MouseEvent] }>();
</script>

<template>
  <button
    :type="props.type"
    :disabled="props.disabled || props.loading"
    :class="['btn', `btn-${props.variant}`, `btn-${props.size}`]"
    @click="(e) => $emit('click', e)"
  >
    <span v-if="props.loading" class="spinner" />
    <slot />
  </button>
</template>

<style scoped>
/* Style direction G (v2.3.2) — Refined :
   reprend la personnalite or de C mais SANS uppercase, SANS gradient,
   coins 8px, ombre or chaleureuse au hover, press translateY 1px.
   Compromis : moderne sans rompre avec l'identite Regent ardoise+or. */
.btn {
  font-family: var(--font-sans);
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition:
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.12s ease,
    border-color 0.18s ease,
    color 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  white-space: nowrap;
}
.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.btn:active:not(:disabled) {
  transform: translateY(1px);
}
.btn:focus-visible {
  outline: 2px solid var(--color-or);
  outline-offset: 2px;
}

.btn-sm {
  font-size: 0.85rem;
  padding: 0.5rem 0.875rem;
  min-height: 36px;
}
.btn-md {
  font-size: 0.95rem;
  padding: 0.65rem 1.25rem;
}
.btn-lg {
  font-size: 1.05rem;
  padding: 0.85rem 1.75rem;
}

/* Primary : or solide + reflet inset + ombre or au hover */
.btn-primary {
  background: var(--color-or);
  color: var(--color-navy);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 1px 3px rgba(0, 0, 0, 0.18);
}
.btn-primary:hover:not(:disabled) {
  background: var(--color-or-light);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    0 4px 12px rgba(232, 160, 32, 0.25);
}

/* Secondary : fond tres leger + bordure or subtile */
.btn-secondary {
  background: rgba(255, 255, 255, 0.04);
  color: var(--color-cream);
  border-color: rgba(232, 160, 32, 0.3);
}
.btn-secondary:hover:not(:disabled) {
  background: rgba(232, 160, 32, 0.08);
  border-color: var(--color-or);
}

/* Ghost : transparent, hover or-tinte */
.btn-ghost {
  background: transparent;
  color: var(--color-cream);
}
.btn-ghost:hover:not(:disabled) {
  background: rgba(232, 160, 32, 0.08);
  color: var(--color-or);
}

/* Danger : rouge solide + reflet inset + ombre rouge au hover */
.btn-danger {
  background: var(--color-danger-strong);
  color: #fff;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 1px 3px rgba(0, 0, 0, 0.2);
}
.btn-danger:hover:not(:disabled) {
  background: #c8535a;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 4px 12px rgba(215, 106, 114, 0.25);
}

/* ============================================================
 * Mode CLAIR : secondary + ghost partent de bases sombres,
 * adapter pour rester lisibles sur fond creme.
 * S'active si data-theme="light" OU data-theme="system" + OS clair.
 * ============================================================ */
:root[data-theme="light"] .btn-secondary {
  background: rgba(28, 35, 51, 0.04);
  color: var(--color-cream);
  border-color: rgba(28, 35, 51, 0.18);
}
:root[data-theme="light"] .btn-secondary:hover:not(:disabled) {
  background: rgba(232, 160, 32, 0.08);
  border-color: var(--color-or);
}
:root[data-theme="light"] .btn-ghost {
  color: var(--color-cream);
}
:root[data-theme="light"] .btn-ghost:hover:not(:disabled) {
  background: rgba(232, 160, 32, 0.1);
  color: #b87a14;
}

@media (prefers-color-scheme: light) {
  :root[data-theme="system"] .btn-secondary {
    background: rgba(28, 35, 51, 0.04);
    color: var(--color-cream);
    border-color: rgba(28, 35, 51, 0.18);
  }
  :root[data-theme="system"] .btn-secondary:hover:not(:disabled) {
    background: rgba(232, 160, 32, 0.08);
    border-color: var(--color-or);
  }
  :root[data-theme="system"] .btn-ghost {
    color: var(--color-cream);
  }
  :root[data-theme="system"] .btn-ghost:hover:not(:disabled) {
    background: rgba(232, 160, 32, 0.1);
    color: #b87a14;
  }
}

/* Spinner : ring rotatif */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
